/**
 * Quick Login One Tap JavaScript
 * Auto-show login prompt after 5 seconds for non-logged users
 * Integrates with Growave social login
 */

// 缓存应用内浏览器检测正则表达式（避免每次调用重新创建）
const IN_APP_BROWSER_PATTERNS = [
  /MicroMessenger/i,          // 微信浏览器
  /FB_IAB|FBAN|FBAV/i,        // Facebook应用内浏览器
  /Instagram/i,               // Instagram应用内浏览器
  /TikTok|BytedanceWebview/i, // TikTok应用内浏览器
  /Twitter/i,                 // Twitter应用内浏览器
  /Line/i,                    // Line应用内浏览器
  /QQ\//i,                    // QQ浏览器
  /WeiBo/i,                   // 微博应用
  /Snapchat/i,                // Snapchat应用
  /LinkedInApp/i,             // LinkedIn应用
  /WhatsApp/i,                // WhatsApp应用
  /Telegram/i,                // Telegram应用
  /YahooJapan/i,              // Yahoo Japan应用
  /WebView|wv/i,              // 通用WebView标识
];

class QuickLoginOneTap {
  constructor() {
    this.container = null;
    this.closeBtn = null;
    this.isVisible = false;
    this.autoShowDelay = 5000; // 5 seconds
    this.autoShowTimer = null;
    this.storageKey = 'quickLoginDismissed';
    this.sessionKey = 'quickLoginShown'; // 保留以兼容旧代码
    this._resizeTimeout = null; // 防抖计时器
    this.init();
  }

  init() {
    // 等待 DOM 加载完成
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.container = document.getElementById('quick-login-onetap');
    if (!this.container) {
      return;
    }

    this.closeBtn = this.container.querySelector('.quick-login-close');
    this.updateHeaderHeight();

    // 根据浏览器类型调整登录按钮显示
    this.adjustLoginButtons();

    this.bindEvents();

    // 检查是否应该显示
    if (this.shouldShow()) {
      this.scheduleAutoShow();
    }
  }

  updateHeaderHeight() {
    // 动态获取header高度，包括announcement bar
    let totalHeight = 0;

    // 获取announcement bar高度
    const announcementBar = document.querySelector('.t4s-announcement-bar');
    if (announcementBar) {
      totalHeight += announcementBar.offsetHeight;
    }

    // 获取header高度
    const header = document.querySelector('.t4s-section-header') ||
                  document.querySelector('header') ||
                  document.querySelector('.t4s-header__wrapper');

    if (header) {
      totalHeight += header.offsetHeight;
    }

    // 如果没有找到任何元素，使用默认值
    if (totalHeight === 0) {
      totalHeight = 80;
    }

    document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
  }

  /**
   * 检测是否为应用内浏览器
   * @returns {boolean} 如果是应用内浏览器返回 true
   */
  isInAppBrowser() {
    const userAgent = navigator.userAgent || '';

    // 使用缓存的正则数组
    return IN_APP_BROWSER_PATTERNS.some(pattern => pattern.test(userAgent));
  }

  /**
   * 根据浏览器类型调整显示的登录按钮
   */
  adjustLoginButtons() {
    const googleButton = this.container.querySelector('.quick-login-btn--google');

    if (this.isInAppBrowser()) {
      // 如果是应用内浏览器，隐藏Google登录按钮
      if (googleButton) {
        googleButton.style.display = 'none';
        googleButton.setAttribute('data-hidden-in-app', 'true');
      }

      // 在容器上添加标识类
      this.container.classList.add('in-app-browser');
    } else {
      // 如果是普通浏览器，确保Google按钮显示
      if (googleButton) {
        googleButton.style.display = '';
        googleButton.removeAttribute('data-hidden-in-app');
      }

      // 移除标识类
      this.container.classList.remove('in-app-browser');
    }
  }

  shouldShow() {
    // 只检查用户是否主动关闭过
    // 如果用户在24小时内主动关闭过，则不显示
    const dismissed = localStorage.getItem(this.storageKey);
    if (dismissed) {
      const dismissTime = parseInt(dismissed);
      const now = Date.now();
      // 24小时 = 24 * 60 * 60 * 1000ms
      if (now - dismissTime < 24 * 60 * 60 * 1000) {
        return false;
      }
    }

    // 移除了sessionStorage的检查，这样刷新页面后会继续显示
    return true;
  }

  scheduleAutoShow() {
    // 设置5秒后自动显示
    this.autoShowTimer = setTimeout(() => {
      this.show();
      // 不再设置sessionStorage，允许刷新后继续显示
    }, this.autoShowDelay);
  }

  show() {
    if (!this.container || this.isVisible) return;

    this.isVisible = true;
    this.container.style.display = 'block';
    
    // 使用 requestAnimationFrame 确保动画正常
    requestAnimationFrame(() => {
      this.container.classList.add('is-visible', 'auto-show');
    });

    // 触发自定义事件
    this.container.dispatchEvent(new CustomEvent('quickLoginShow'));
  }

  hide(isUserAction = true) {
    if (!this.container || !this.isVisible) return;

    this.isVisible = false;

    // 触发隐藏事件
    this.container.dispatchEvent(new CustomEvent('quickLoginHide'));

    // 开始隐藏动画
    this.container.classList.remove('is-visible', 'auto-show');

    // 动画完成后完全隐藏
    setTimeout(() => {
      if (!this.isVisible) {
        this.container.style.display = 'none';
        this.container.style.visibility = 'hidden';
      }
    }, 350);

    // 记录用户关闭行为
    if (isUserAction) {
      localStorage.setItem(this.storageKey, Date.now().toString());
    }
  }

  bindEvents() {
    // 绑定关闭按钮
    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this.handleCloseClick);
      this.handleCloseClick = () => this.hide(true);
      this.closeBtn.addEventListener('click', this.handleCloseClick);
    }

    // 移动端交互
    if (window.innerWidth <= 768) {
      // 点击背景遮罩关闭
      this.container.addEventListener('click', (e) => {
        if (e.target === this.container) {
          this.hide(true);
        }
      });
      
      // 防止滚动传播
      const loginContainer = this.container.querySelector('.quick-login-container');
      if (loginContainer) {
        loginContainer.addEventListener('touchmove', (e) => {
          e.stopPropagation();
        }, { passive: true });
      }

      this.addSwipeToClose();
    }

    // ESC键关闭
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hide(true);
      }
    });

    // 绑定社交登录按钮
    // Inline snippet already handles provider clicks (AB tags + tracking), avoid double-binding.
    if (!window.__quickLoginOneTapInline) {
      this.bindSocialLogin();
    }

    // 页面离开时清理
    window.addEventListener('beforeunload', () => {
      if (this.autoShowTimer) {
        clearTimeout(this.autoShowTimer);
      }
    });

    // 窗口大小变化时重新计算header高度（防抖处理）
    window.addEventListener('resize', () => {
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = setTimeout(() => {
        this.updateHeaderHeight();
      }, 150);
    });

    // 页面可见性变化处理
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.autoShowTimer) {
        clearTimeout(this.autoShowTimer);
      } else if (!document.hidden && this.shouldShow() && !this.isVisible) {
        this.scheduleAutoShow();
      }
    });
  }

  addSwipeToClose() {
    const loginContainer = this.container.querySelector('.quick-login-container');
    if (!loginContainer) return;
    
    let startY = 0;
    let currentY = 0;
    let isDragging = false;
    
    const handleTouchStart = (e) => {
      startY = e.touches[0].clientY;
      isDragging = true;
      loginContainer.style.transition = 'none';
    };
    
    const handleTouchMove = (e) => {
      if (!isDragging) return;
      
      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      
      // 只允许向下拖动
      if (deltaY > 0) {
        loginContainer.style.transform = `translateY(${deltaY}px)`;
      }
    };
    
    const handleTouchEnd = (e) => {
      if (!isDragging) return;
      
      isDragging = false;
      loginContainer.style.transition = '';
      
      const deltaY = currentY - startY;
      
      // 向下拖动超过100px则关闭
      if (deltaY > 100) {
        this.hide(true);
      } else {
        loginContainer.style.transform = '';
      }
      
      startY = 0;
      currentY = 0;
    };

    loginContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    loginContainer.addEventListener('touchmove', handleTouchMove, { passive: true });
    loginContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
  }

  bindSocialLogin() {
    const socialBtns = this.container.querySelectorAll('.quick-login-btn');
    
    socialBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        
        const provider = btn.dataset.provider;
        if (!provider) return;

        // 添加加载状态
        btn.classList.add('loading');
        
        // 集成 Growave 社交登录
        this.handleSocialLogin(provider, btn);
      });
    });
  }

  handleSocialLogin(provider, buttonElement) {
    try {
      // 检查 Growave SSW 是否可用
      if (typeof window.ssw !== 'undefined' && window.ssw.socialLogin) {
        // 使用 Growave 的社交登录功能
        window.ssw.socialLogin.login(provider, {
          onSuccess: (response) => {
            console.log('Social login successful:', response);
            // 登录成功，隐藏提示框（非用户主动关闭）
            this.hide(false);
            // 刷新页面或跳转
            window.location.reload();
          },
          onError: (error) => {
            console.error('Social login error:', error);
            buttonElement.classList.remove('loading');
            this.showError('登录失败，请稍后重试');
          }
        });
      } else {
        // 备用方案：直接跳转到对应的登录URL
        // 对于移动端苹果登录，立即跳转不需要延迟
        if (provider === 'apple' && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
          buttonElement.classList.remove('loading');
        }
        this.fallbackSocialLogin(provider);
      }
    } catch (error) {
      console.error('Social login integration error:', error);
      buttonElement.classList.remove('loading');
      this.fallbackSocialLogin(provider);
    }
  }

  fallbackSocialLogin(provider) {
    // 备用登录方案 - 使用实际OAuth端点
    const loginUrls = {
      google: 'https://user.wayofwade.com/api/auth/google',
      apple: 'https://user.wayofwade.com/api/auth/apple',
      meta: 'https://user.wayofwade.com/api/auth/meta'
    };

    const url = loginUrls[provider];

    // 对于苹果登录，需要添加redirect_uri参数以确保正确返回
    if (provider === 'apple') {
      // 获取当前页面URL作为返回地址
      const currentUrl = window.location.href;
      const returnUrl = encodeURIComponent(currentUrl);

      // 如果是移动端Safari或应用内浏览器，使用当前窗口
      const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

      if (isMobileSafari) {
        // 移动端直接在当前窗口打开，确保可以正确返回
        window.location.href = `${url}&redirect_uri=${returnUrl}`;
        return;
      }
    }

    // 延迟1秒让用户看到loading动画，然后清除并跳转
    setTimeout(() => {
      // 清除所有loading状态
      const buttons = this.container.querySelectorAll('.quick-login-btn.loading');
      buttons.forEach(btn => btn.classList.remove('loading'));

      // 跳转
      if (url) {
        window.location.href = url;
      } else {
        window.location.href = '/account/login';
      }
    }, 1000);
  }

  showError(message) {
    // 简单的错误提示
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ff4444;
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      animation: fadeIn 0.3s ease;
    `;
    errorDiv.textContent = message;
    document.body.appendChild(errorDiv);

    setTimeout(() => {
      errorDiv.remove();
    }, 3000);
  }

  // 公共方法：手动显示
  manualShow() {
    if (this.autoShowTimer) {
      clearTimeout(this.autoShowTimer);
    }
    this.show();
  }

  // 公共方法：重置显示状态（用于测试）
  reset() {
    localStorage.removeItem(this.storageKey);
    // sessionStorage已经不再使用
    if (this.autoShowTimer) {
      clearTimeout(this.autoShowTimer);
    }
    this.hide(false); // 不记录为用户主动关闭
  }

  // 公共方法：测试浏览器识别功能
  testBrowserDetection() {
    const isInApp = this.isInAppBrowser();
    const googleButton = this.container?.querySelector('.quick-login-btn--google');
    
    console.group('🔍 Browser Detection Test');
    console.log('User-Agent:', navigator.userAgent);
    console.log('Is In-App Browser:', isInApp);
    console.log('Google Button Found:', !!googleButton);
    console.log('Google Button Hidden:', googleButton?.style.display === 'none');
    console.log('Container Classes:', this.container?.className);
    console.groupEnd();
    
    return {
      userAgent: navigator.userAgent,
      isInApp,
      googleButtonFound: !!googleButton,
      googleButtonHidden: googleButton?.style.display === 'none',
      containerClasses: this.container?.className
    };
  }

  // 公共方法：模拟不同浏览器环境（用于测试）
  simulateBrowser(userAgentString) {
    // 临时修改 User-Agent 字符串用于测试
    Object.defineProperty(navigator, 'userAgent', {
      value: userAgentString,
      configurable: true
    });
    
    console.log('🧪 Simulating browser with User-Agent:', userAgentString);
    
    // 重新调整按钮显示
    this.adjustLoginButtons();
    
    // 运行测试
    return this.testBrowserDetection();
  }
}

// 初始化 Quick Login One Tap
let quickLoginOneTap;

// 确保在 DOM 加载后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    quickLoginOneTap = new QuickLoginOneTap();
  });
} else {
  quickLoginOneTap = new QuickLoginOneTap();
}

// 将实例暴露给全局作用域
window.QuickLoginOneTap = QuickLoginOneTap;
window.quickLoginOneTap = quickLoginOneTap;

// Track post-redirect auth success (mainly social login) so we can compute conversion by variant.
(function () {
  const AUTH_ATTEMPT_STORAGE_KEY = 'quick_login_auth_attempt';
  const AUTH_ATTEMPT_TTL_MS = 10 * 60 * 1000; // 10 minutes
  const QUICK_LOGIN_AB_TEST_NAME = 'quick_login_v1_vs_v2';
  const EPOCH_SECONDS_MIN_2000 = 946684800; // 2000-01-01 in seconds
  const EPOCH_MS_MIN_2000 = 946684800000; // 2000-01-01 in ms
  const ALLOWED_FUTURE_SKEW_MS = 5 * 60 * 1000; // 5 minutes

  function getDeviceType() {
    return window.innerWidth <= 768 ? 'mobile' : 'desktop';
  }

  function toEpochMs(value) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return null;
    if (numericValue >= EPOCH_MS_MIN_2000) return numericValue;
    if (numericValue >= EPOCH_SECONDS_MIN_2000) return numericValue * 1000;
    return null;
  }

  function sanitizeEpochMs(epochMs) {
    if (!Number.isFinite(epochMs)) return null;
    const now = Date.now();
    if (epochMs > now + ALLOWED_FUTURE_SKEW_MS) return null;
    // Handle small clock skew (server time slightly ahead of client).
    return epochMs > now ? now : epochMs;
  }

  function safeParseJson(value) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return null;
    }
  }

  function getShopifyCustomerId() {
    try {
      return window.ShopifyAnalytics?.meta?.page?.customerId || null;
    } catch (e) {
      return null;
    }
  }

  function getQuickLoginCustomerMeta() {
    try {
      if (window.__quickLoginCustomer && typeof window.__quickLoginCustomer === 'object') {
        return window.__quickLoginCustomer;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  function getCustomerCreatedAtMs() {
    const meta = getQuickLoginCustomerMeta();
    if (!meta) return null;
    const rawCreatedAtMs = meta.created_at_ms;
    if (rawCreatedAtMs === null || typeof rawCreatedAtMs === 'undefined') return null;
    if (rawCreatedAtMs === '' || rawCreatedAtMs === 'null') return null;

    const createdAtMs = sanitizeEpochMs(toEpochMs(rawCreatedAtMs));
    return Number.isFinite(createdAtMs) ? createdAtMs : null;
  }

  function getCustomerOrdersCount() {
    const meta = getQuickLoginCustomerMeta();
    if (!meta) return null;
    const rawOrdersCount = meta.orders_count;
    if (rawOrdersCount === null || typeof rawOrdersCount === 'undefined') return null;
    const ordersCount = Number(rawOrdersCount);
    return Number.isFinite(ordersCount) && ordersCount >= 0 ? ordersCount : null;
  }

  function customerHasTagForAttemptTag(customerMeta, attemptTag) {
    if (!customerMeta || !attemptTag) return false;
    const tag = String(attemptTag).trim();
    if (!tag) return false;

    if (tag === 'testcouponv2') return customerMeta.has_tag_testcouponv2 === true;
    if (tag === 'loginv1') return customerMeta.has_tag_loginv1 === true;
    return false;
  }

  function inferIsNewCustomerFromTags({ attemptTag, customerMeta }) {
    if (!customerMeta) return null;

    if (!customerHasTagForAttemptTag(customerMeta, attemptTag)) return null;

    const ordersCount = Number(customerMeta.orders_count);
    if (!Number.isFinite(ordersCount) || ordersCount < 0) return null;

    // Heuristic: tags are expected to be applied on account creation; combine with "no orders" for safety.
    return ordersCount === 0 ? 1 : 0;
  }

  function inferIsNewCustomer({ customerCreatedAtMs, attemptStartedAtMs }) {
    const normalizedCreatedAtMs = sanitizeEpochMs(toEpochMs(customerCreatedAtMs));
    const normalizedAttemptStartedAtMs = sanitizeEpochMs(toEpochMs(attemptStartedAtMs));
    if (!Number.isFinite(normalizedCreatedAtMs) || !Number.isFinite(normalizedAttemptStartedAtMs)) return null;

    const accountAgeMs = Date.now() - normalizedCreatedAtMs;
    if (!Number.isFinite(accountAgeMs) || accountAgeMs < 0) return null;

    const createdAfterAttempt = normalizedCreatedAtMs >= normalizedAttemptStartedAtMs - 2 * 60 * 1000;
    if (!createdAfterAttempt) return 0;
    return accountAgeMs <= AUTH_ATTEMPT_TTL_MS ? 1 : 0;
  }

  function isCustomerLoggedIn() {
    const meta = getQuickLoginCustomerMeta();
    if (meta && meta.is_logged_in === true) return true;
    if (getShopifyCustomerId()) return true;
    if (document.querySelector('a[href^=\"/account/logout\"], a[href*=\"/account/logout\"]')) return true;
    return false;
  }

  function trackQuickLoginEvent(eventName, params = {}) {
    const baseParams = {
      device: getDeviceType(),
      timestamp: Date.now()
    };

    const eventParams = { ...baseParams, ...params };

    // OpenPanel
    if (window.op) {
      try { window.op('track', eventName, eventParams); } catch (e) {}
    }

    // GA4
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', eventName, eventParams); } catch (e) {}
    } else if (typeof gtag !== 'undefined') {
      try { gtag('event', eventName, eventParams); } catch (e) {}
    }

    // DataLayer for GTM
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...eventParams
    });

    // Microsoft Clarity
    if (typeof window.clarity === 'function') {
      try {
        window.clarity('set', 'quick_login_ab_test', String(eventParams.ab_test_name || QUICK_LOGIN_AB_TEST_NAME));
        if (eventParams.version) {
          window.clarity('set', 'quick_login_variant', String(eventParams.version));
        }
        window.clarity('set', 'quick_login_device', String(eventParams.device || ''));

        if (eventParams.source) {
          window.clarity('set', 'quick_login_source', String(eventParams.source));
        }
        if (eventParams.tags) {
          window.clarity('set', 'quick_login_tags', String(eventParams.tags));
        }

        if (eventParams.provider) {
          window.clarity('set', 'quick_login_provider', String(eventParams.provider));
        }
        if (eventParams.auth_method) {
          window.clarity('set', 'quick_login_auth_method', String(eventParams.auth_method));
        }
        if (typeof eventParams.is_new_customer !== 'undefined') {
          window.clarity('set', 'quick_login_is_new_customer', String(eventParams.is_new_customer));
        }
        if (eventParams.is_new_customer_infer_source) {
          window.clarity('set', 'quick_login_is_new_customer_infer_source', String(eventParams.is_new_customer_infer_source));
        }
        if (eventParams.customer_created_at_source) {
          window.clarity('set', 'quick_login_customer_created_at_source', String(eventParams.customer_created_at_source));
        }

        window.clarity('event', String(eventName));
      } catch (e) {}
    }
  }

  function readAuthAttempt() {
    try {
      const raw = localStorage.getItem(AUTH_ATTEMPT_STORAGE_KEY);
      return raw ? safeParseJson(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function clearAuthAttempt() {
    try {
      localStorage.removeItem(AUTH_ATTEMPT_STORAGE_KEY);
    } catch (e) {}
  }

  function extractQuickLoginCustomerMetaFromHtml(html) {
    try {
      if (!html || typeof html !== 'string') return null;

      const blockMatch = html.match(/window\.__quickLoginCustomer\s*=\s*\{[\s\S]*?\};/);
      if (!blockMatch) return null;
      const block = blockMatch[0];

      const createdAtMatch = block.match(/created_at_ms:\s*(null|\d+)/i);
      const ordersCountMatch = block.match(/orders_count:\s*(null|\d+)/i);
      const hasTagV2Match = block.match(/has_tag_testcouponv2:\s*(true|false)/i);
      const hasTagV1Match = block.match(/has_tag_loginv1:\s*(true|false)/i);

      const createdAtMs = createdAtMatch && createdAtMatch[1] !== 'null'
        ? sanitizeEpochMs(toEpochMs(createdAtMatch[1]))
        : null;
      const ordersCount = ordersCountMatch && ordersCountMatch[1] !== 'null'
        ? Number(ordersCountMatch[1])
        : null;

      const meta = {};

      if (Number.isFinite(createdAtMs)) meta.created_at_ms = createdAtMs;
      if (Number.isFinite(ordersCount) && ordersCount >= 0) meta.orders_count = ordersCount;
      if (hasTagV2Match) meta.has_tag_testcouponv2 = hasTagV2Match[1] === 'true';
      if (hasTagV1Match) meta.has_tag_loginv1 = hasTagV1Match[1] === 'true';

      return Object.keys(meta).length > 0 ? meta : null;
    } catch (e) {
      return null;
    }
  }

  function extractCustomerCreatedAtMsFromHtml(html) {
    try {
      if (!html || typeof html !== 'string') return null;
      const match = html.match(/__quickLoginCustomer\s*=\s*\{[\s\S]*?created_at_ms:\s*(null|\d+)/i);
      if (!match) return null;
      if (match[1] === 'null') return null;
      const createdAtMs = sanitizeEpochMs(toEpochMs(match[1]));
      return Number.isFinite(createdAtMs) ? createdAtMs : null;
    } catch (e) {
      return null;
    }
  }

  async function fetchQuickLoginCustomerMetaFromAccountPage() {
    try {
      const response = await fetch('/account', { credentials: 'same-origin', cache: 'no-store' });
      const finalUrl = response.url || '';
      if (finalUrl.includes('/account/login')) return null;
      if (!response.ok) return null;
      const html = await response.text();
      return extractQuickLoginCustomerMetaFromHtml(html);
    } catch (e) {
      return null;
    }
  }

  async function fetchCustomerCreatedAtMsFromAccountPage() {
    try {
      const response = await fetch('/account', { credentials: 'same-origin', cache: 'no-store' });
      const finalUrl = response.url || '';
      if (finalUrl.includes('/account/login')) return null;
      if (!response.ok) return null;
      const html = await response.text();
      return extractCustomerCreatedAtMsFromHtml(html);
    } catch (e) {
      return null;
    }
  }

  let isProcessingAttempt = false;

  async function processAuthAttempt() {
    const attempt = readAuthAttempt();
    if (!attempt || typeof attempt !== 'object') return;

    const startedAt = Number(attempt.started_at || 0);
    if (!startedAt) {
      clearAuthAttempt();
      return;
    }

    const ageMs = Date.now() - startedAt;
    if (ageMs > AUTH_ATTEMPT_TTL_MS) {
      clearAuthAttempt();
      return;
    }

    if (!isCustomerLoggedIn()) return;
    if (isProcessingAttempt) return;
    isProcessingAttempt = true;

    try {
      let customerMeta = getQuickLoginCustomerMeta() || {};
      let customerOrdersCount = getCustomerOrdersCount();

      let customerCreatedAtMs = getCustomerCreatedAtMs();
      let customerCreatedAtSource = Number.isFinite(customerCreatedAtMs) ? 'page_liquid' : null;

      if (!Number.isFinite(customerCreatedAtMs) || typeof customerOrdersCount !== 'number') {
        const accountMeta = await fetchQuickLoginCustomerMetaFromAccountPage();
        if (accountMeta && typeof accountMeta === 'object') {
          customerMeta = { ...customerMeta, ...accountMeta };
          if (!Number.isFinite(customerCreatedAtMs) && Number.isFinite(accountMeta.created_at_ms)) {
            customerCreatedAtMs = accountMeta.created_at_ms;
            customerCreatedAtSource = 'account_fetch';
          }
          if (typeof customerOrdersCount !== 'number' && typeof accountMeta.orders_count === 'number') {
            customerOrdersCount = accountMeta.orders_count;
          }
        }
      }

      if (!Number.isFinite(customerCreatedAtMs)) {
        customerCreatedAtMs = await fetchCustomerCreatedAtMsFromAccountPage();
        if (Number.isFinite(customerCreatedAtMs)) customerCreatedAtSource = 'account_fetch';
      }

      let inferredIsNewCustomer = inferIsNewCustomer({
        customerCreatedAtMs: customerCreatedAtMs ?? NaN,
        attemptStartedAtMs: startedAt
      });
      let inferredIsNewCustomerSource = typeof inferredIsNewCustomer === 'number' ? 'created_at' : null;

      if (typeof inferredIsNewCustomer !== 'number') {
        inferredIsNewCustomer = inferIsNewCustomerFromTags({
          attemptTag: attempt.tags,
          customerMeta
        });
        if (typeof inferredIsNewCustomer === 'number') {
          inferredIsNewCustomerSource = 'tag_orders_count';
        }
      }

      // If we can't read the real customer creation time on the storefront,
      // keep tracking deterministic by optionally using the attempt start time
      // as an estimate for newly-created accounts.
      let customerCreatedAtMsAvailable = Number.isFinite(customerCreatedAtMs);
      let customerCreatedAtMsEstimated = false;

      if (!customerCreatedAtMsAvailable && inferredIsNewCustomer === 1) {
        customerCreatedAtMs = startedAt;
        customerCreatedAtSource = 'approx_attempt_started_at';
        customerCreatedAtMsAvailable = Number.isFinite(customerCreatedAtMs);
        customerCreatedAtMsEstimated = true;
      }

      const accountAgeMs = Number.isFinite(customerCreatedAtMs) ? (Date.now() - customerCreatedAtMs) : null;

      const payload = {
        attempt_id: attempt.id || '',
        version: attempt.version || '',
        ab_test_name: attempt.ab_test_name || QUICK_LOGIN_AB_TEST_NAME,
        ab_variant: attempt.version || '',
        auth_method: attempt.auth_method || 'social',
        provider: attempt.provider || '',
        tags: attempt.tags || '',
        source: attempt.source || 'quick_login_popup',
        time_to_success_ms: ageMs,
        origin_path: attempt.origin_path || '',
        landing_path: window.location.pathname
      };

      if (customerCreatedAtSource) {
        payload.customer_created_at_source = customerCreatedAtSource;
      }

      if (typeof customerOrdersCount === 'number') {
        payload.customer_orders_count = customerOrdersCount;
      }

      if (Number.isFinite(accountAgeMs) && accountAgeMs >= 0) {
        payload.account_age_ms = accountAgeMs;
      }
      if (Number.isFinite(customerCreatedAtMs)) {
        payload.customer_created_at_ms = customerCreatedAtMs;
      }
      payload.customer_created_at_ms_available = customerCreatedAtMsAvailable ? 1 : 0;
      if (customerCreatedAtMsEstimated) {
        payload.customer_created_at_ms_estimated = 1;
      }
      if (typeof inferredIsNewCustomer === 'number') {
        payload.is_new_customer = inferredIsNewCustomer;
        payload.is_new_customer_inferred = 1;
        if (inferredIsNewCustomerSource) {
          payload.is_new_customer_infer_source = inferredIsNewCustomerSource;
        }
      }

      trackQuickLoginEvent('quick_login_auth_success', payload);

      clearAuthAttempt();
    } finally {
      isProcessingAttempt = false;
    }
  }

  // Run once now and once later (ShopifyAnalytics may load late).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processAuthAttempt);
  } else {
    processAuthAttempt();
  }
  setTimeout(processAuthAttempt, 2000);
})();
